import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import useActiveProjectType from '../../hooks/useActiveProjectType';
import { motion } from 'framer-motion';
import {
  User, Building2, Palette, Globe, Upload, RefreshCw,
  Loader2, Check, X, Eye, EyeOff, Save, Trash2, Plus, Edit2, Link2, Unlink,
  Package, Sparkles, Image, Video, Crown, Lock, BookOpen, Target, PenTool,
  Layers, Library, MapPin, ChevronDown, ChevronUp, AlertCircle, ChevronRight, PlusCircle, Type
} from 'lucide-react';
import { FaLinkedin, FaPinterest, FaInstagram, FaFacebook, FaTwitter, FaBlog, FaEnvelope, FaNewspaper, FaMedium, FaYoutube, FaBox, FaTiktok } from 'react-icons/fa';
import { PLATFORM_LANG_LIST, localizePlatformLabel } from '../../lib/platforms';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import WebsiteIntegrationCard from '../../components/WebsiteIntegrationCard';
import LogoPlacementEditor from '../../components/LogoPlacementEditor';
import BrandMediaManager from '../../components/BrandMediaManager';
import BrandDnaView from './BrandDnaView';
import StrategyView from './StrategyView';
import KnowledgeView from './KnowledgeView';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import BillingTab from './BillingTab';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

// ── Shared UI Components ──────────────────────────────────────────────────────

const TagInput = ({ tags, onChange, placeholder, color = '#00D9C8', testId }) => {
  const [input, setInput] = useState('');
  const addTag = () => {
    const val = input.trim();
    if (val && !tags.includes(val)) { onChange([...tags, val]); setInput(''); }
  };
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {tags.map((tag, i) => (
          <span key={i} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full"
            style={{ background: `${color}15`, color, border: `1px solid ${color}30` }}>
            {tag}
            <button onClick={() => onChange(tags.filter((_, j) => j !== i))} className="hover:opacity-70"><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
          placeholder={placeholder} className="bg-[#0A1628] border-transparent text-white text-sm flex-1" data-testid={testId} />
        <Button type="button" onClick={addTag} disabled={!input.trim()} variant="ghost" size="sm" className="text-[#5A6C7D] hover:text-white px-2">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

const CollapsibleSection = ({ title, icon: Icon, iconColor, children, defaultOpen = false, badge }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="bg-[#1A2B42] border-transparent">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-4 hover:bg-[#1A2B42]/80 transition-colors">
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5" style={{ color: iconColor }} />
          <span className="text-white font-medium text-sm">{title}</span>
          {badge && <span className="text-[10px] bg-[#00D9C8]/10 text-[#00D9C8] px-2 py-0.5 rounded-full">{badge}</span>}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-[#5A6C7D]" /> : <ChevronDown className="w-4 h-4 text-[#5A6C7D]" />}
      </button>
      {open && <CardContent className="px-5 pb-5 pt-0 border-t border-[#0A1628]/50">{children}</CardContent>}
    </Card>
  );
};


// ── Marke Sub-Components ──────────────────────────────────────────────────────

// DNA_DIMENSIONS + DNADimensionRow + BrandDNAUebersicht moved to
// components/BrandDnaDimensionsEditor.jsx (imported at top; shared with BrandDnaView).

const BrandSchreibstil = ({ getAuthHeader, contentLanguage, onSavePreferences, platformLanguages, onSavePlatformLanguages }) => {
  const { t, lang } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [writing, setWriting] = useState({
    addressing: 'Sie', tone: '', formalityLevel: 'neutral', emojiUsage: 'sparsam',
    recurringPhrases: [], favoriteWords: [], postOpeningPatterns: [], forbiddenElements: [], doList: [], dontList: [],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/brand-dna`, { headers: getAuthHeader() })
      .then(r => {
        // Neu: strukturiertes Objekt hat Vorrang
        const structured = r.data.brandDNA || r.data.structured_dna;
        const vt = structured?.voice_and_tone || {};
        const cr = structured?.copy_rules || {};
        const cv = structured?.core_vocabulary || {};

        // Legacy als Fallback
        const w = r.data.writingStyle || {};
        const emojiStrategy = w.emojiUsage?.examples || w.emojiExamples || [];

        setWriting({
          addressing:          vt.address_form     || w.addressing || 'Sie',
          tone:                (vt.personality_traits||[]).join(', ') || w.tone || '',
          formalityLevel:      vt.formality_level  || w.formalityLevel || 'neutral',
          emojiUsage:          w.emojiUsage?.frequency || w.emojiUsage || 'sparsam',
          emojiExamples:       Array.isArray(emojiStrategy) ? emojiStrategy : [],
          recurringPhrases:    (vt.reference_copy_positive||[]).map(e=>e.text).filter(Boolean) || w.recurringPhrases || [],
          favoriteWords:       cv.must_use         || w.favoriteWords || [],
          postOpeningPatterns: w.postOpeningPatterns || [],
          forbiddenElements:   cv.forbidden        || w.forbiddenElements || [],
          doList:              cr.dos              || w.doList || [],
          dontList:            cr.donts            || w.dontList || [],
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/brand-dna/writing-style`,
        { ...writing, emojiUsage: { frequency: writing.emojiUsage } }, { headers: getAuthHeader() });
      toast.success(t.settings.writing.savedToast);
    } catch { toast.error(t.settings.writing.saveFailed); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#00D9C8]" /></div>;

  return (
    <div className="space-y-4">
      {/* Content-Sprache — oben im Schreibstil */}
      <Card className="bg-[#1A2B42] border-transparent">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center gap-2"><Globe className="w-4 h-4 text-[#00D9C8]" />{t.settings.writing.contentLanguage}</CardTitle>
          <CardDescription className="text-[#A0AEC0] text-sm">{t.settings.writing.contentLanguageDesc}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'de', label: 'Deutsch' }, { key: 'en', label: 'English' },
              { key: 'pl', label: 'Polski' }, { key: 'fr', label: 'Français' },
              { key: 'es', label: 'Español' }, { key: 'it', label: 'Italiano' },
              { key: 'nl', label: 'Nederlands' }, { key: 'pt', label: 'Português' },
              { key: 'cs', label: 'Čeština' }, { key: 'hu', label: 'Magyar' },
              { key: 'ro', label: 'Română' },
              { key: 'lv', label: 'Latviešu' },
              { key: 'sv', label: 'Svenska' }, { key: 'tr', label: 'Türkçe' },
            ].map(l => (
              <button key={l.key} onClick={() => onSavePreferences(l.key, null)}
                className={`py-2 px-3 rounded-lg text-sm font-medium transition-all border ${contentLanguage === l.key ? 'bg-[#00D9C8]/20 text-[#00D9C8] border-[#00D9C8]/40' : 'bg-[#0A1628] text-[#5A6C7D] border-transparent hover:text-[#A0AEC0]'}`}
                data-testid={`settings-lang-${l.key}`}>{l.label}</button>
            ))}
          </div>

          {/* Per-platform language overrides */}
          <div className="border-t border-[#0A1628] pt-3">
            <p className="text-xs text-[#5A6C7D] mb-3">{t.settings.writing.platformMapping}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {PLATFORM_LANG_LIST.map(pl => {
                const Icon = pl.icon;
                const currentLang = platformLanguages?.[pl.key] || '';
                return (
                  <div key={pl.key} className="flex items-center gap-2 bg-[#0A1628] rounded-lg px-3 py-2" data-testid={`platform-lang-${pl.key}`}>
                    <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: pl.color }} />
                    <span className="text-xs text-[#A0AEC0] flex-1 truncate">{localizePlatformLabel(pl, lang)}</span>
                    <div className="flex gap-1">
                      {['de', 'en', 'pl', 'fr', 'es', 'it', 'ro', 'lv'].map(l => (
                        <button
                          key={l}
                          onClick={() => {
                            const updated = { ...platformLanguages };
                            if (currentLang === l) {
                              delete updated[pl.key];
                            } else {
                              updated[pl.key] = l;
                            }
                            onSavePlatformLanguages(updated);
                          }}
                          className={`px-1.5 py-0.5 rounded text-[10px] font-mono transition-all ${
                            currentLang === l
                              ? 'bg-[#00D9C8]/20 text-[#00D9C8] border border-[#00D9C8]/40'
                              : 'text-[#5A6C7D] hover:text-[#A0AEC0]'
                          }`}
                        >
                          {l.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      <CollapsibleSection title={t.settings.writing.addressingAndTone} icon={PenTool} iconColor="#00D9C8" defaultOpen={true}>
        <div className="space-y-4 mt-4">
          {lang === 'de' && (
          <div>
            <label className="text-[#5A6C7D] text-xs block mb-2">{t.settings.writing.salutation}</label>
            <div className="grid grid-cols-3 gap-2">
              {['Du', 'Sie', 'Ihr'].map(opt => (
                <button key={opt} onClick={() => setWriting(w => ({ ...w, addressing: opt }))}
                  className={`py-2 px-3 rounded-lg text-sm font-medium transition-all border ${writing.addressing === opt ? 'bg-[#00D9C8]/20 text-[#00D9C8] border-[#00D9C8]/40' : 'bg-[#0A1628] text-[#5A6C7D] border-transparent hover:text-[#A0AEC0]'}`}
                  data-testid={`addressing-${opt.toLowerCase()}`}>{opt}</button>
              ))}
            </div>
          </div>
          )}
          <div>
            <label className="text-[#5A6C7D] text-xs block mb-1">{t.settings.writing.tonality}</label>
            <Input value={writing.tone} onChange={(e) => setWriting(w => ({ ...w, tone: e.target.value }))}
              placeholder={t.settings.writing.tonalityPlaceholder} className="bg-[#0A1628] border-transparent text-white text-sm" data-testid="writing-tone-input" />
          </div>
          <div>
            <label className="text-[#5A6C7D] text-xs block mb-2">{t.settings.writing.formality}</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'formell', label: t.settings.writing.formalityFormal },
                { value: 'neutral', label: t.settings.writing.formalityNeutral },
                { value: 'informell', label: t.settings.writing.formalityInformal },
              ].map(({ value, label }) => (
                <button key={value} onClick={() => setWriting(w => ({ ...w, formalityLevel: value }))}
                  className={`py-2 px-3 rounded-lg text-xs font-medium transition-all border capitalize ${writing.formalityLevel === value ? 'bg-[#00D9C8]/20 text-[#00D9C8] border-[#00D9C8]/40' : 'bg-[#0A1628] text-[#5A6C7D] border-transparent hover:text-[#A0AEC0]'}`}
                  data-testid={`formality-${value}`}>{label}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[#5A6C7D] text-xs block mb-2">{t.settings.writing.emojiUsage}</label>
            <div className="grid grid-cols-4 gap-2">
              {[
                { value: 'keine', label: t.settings.writing.emojiNone },
                { value: 'sparsam', label: t.settings.writing.emojiSparingly },
                { value: 'moderat', label: t.settings.writing.emojiModerate },
                { value: 'reichlich', label: t.settings.writing.emojiPlentiful },
              ].map(({ value, label }) => (
                <button key={value} onClick={() => setWriting(w => ({ ...w, emojiUsage: value }))}
                  className={`py-2 px-2 rounded-lg text-xs font-medium transition-all border capitalize ${writing.emojiUsage === value ? 'bg-amber-400/20 text-[#00D9C8] border-amber-400/40' : 'bg-[#0A1628] text-[#5A6C7D] border-transparent hover:text-[#A0AEC0]'}`}
                  data-testid={`emoji-${value}`}>{label}</button>
              ))}
            </div>
          {writing.emojiExamples && writing.emojiExamples.length > 0 && (
            <div>
              <label className="text-[#5A6C7D] text-xs block mb-2">{t.settings.writing.emojiFromAnalysis}</label>
              <div className="flex flex-wrap gap-2">
                {writing.emojiExamples.map((emoji, i) => (
                  <span key={i} className="text-lg bg-[#0A1628] px-2 py-1 rounded-lg">{emoji}</span>
                ))}
              </div>
            </div>
          )}
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title={t.settings.writing.phrasesAndPatterns} icon={Target} iconColor="#00D9C8">
        <div className="space-y-4 mt-4">
          <div><label className="text-[#5A6C7D] text-xs block mb-2">{t.settings.writing.recurringPhrases}</label>
            <TagInput tags={writing.recurringPhrases || []} onChange={(v) => setWriting(w => ({ ...w, recurringPhrases: v }))} placeholder={t.settings.writing.recurringPhrasesPh} color="#00D9C8" testId="recurring-phrases-input" /></div>
          <div><label className="text-[#5A6C7D] text-xs block mb-2">{t.settings.writing.favoriteWords}</label>
            <TagInput tags={writing.favoriteWords || []} onChange={(v) => setWriting(w => ({ ...w, favoriteWords: v }))} placeholder={t.settings.writing.favoriteWordsPh} color="#00D9C8" testId="favorite-words-input" /></div>
          <div><label className="text-[#5A6C7D] text-xs block mb-2">{t.settings.writing.postOpenings}</label>
            <TagInput tags={writing.postOpeningPatterns || []} onChange={(v) => setWriting(w => ({ ...w, postOpeningPatterns: v }))} placeholder={t.settings.writing.postOpeningsPh} color="#00D9C8" testId="opening-patterns-input" /></div>
          <div><label className="text-[#5A6C7D] text-xs block mb-2">{t.settings.writing.forbiddenElements}</label>
            <TagInput tags={writing.forbiddenElements || []} onChange={(v) => setWriting(w => ({ ...w, forbiddenElements: v }))} placeholder={t.settings.writing.forbiddenElementsPh} color="#00D9C8" testId="forbidden-elements-input" /></div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title={t.settings.writing.dosAndDonts} icon={Check} iconColor="#00D9C8">
        <div className="space-y-4 mt-4">
          <div><label className="text-[#5A6C7D] text-xs block mb-2">{t.settings.writing.dosLabel}</label>
            <TagInput tags={writing.doList || []} onChange={(v) => setWriting(w => ({ ...w, doList: v }))} placeholder={t.settings.writing.dosPh} color="#00D9C8" testId="do-list-input" /></div>
          <div><label className="text-[#5A6C7D] text-xs block mb-2">{t.settings.writing.dontsLabel}</label>
            <TagInput tags={writing.dontList || []} onChange={(v) => setWriting(w => ({ ...w, dontList: v }))} placeholder={t.settings.writing.dontsPh} color="#00D9C8" testId="dont-list-input" /></div>
        </div>
      </CollapsibleSection>

      <Button onClick={save} disabled={saving} className="w-full bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] font-semibold" data-testid="save-writing-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}{t.settings.writing.saveWriting}
      </Button>
    </div>
  );
};

// PLATFORM_LANG_LIST + localizePlatformLabel moved to lib/platforms.js (imported at top).

const BrandBildsprache = ({ getAuthHeader, imageStylePref, onSavePreferences }) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [visual, setVisual] = useState({ primaryColors: [], visualType: '', mood: [] });
  const [saving, setSaving] = useState(false);
  const [newColorHex, setNewColorHex] = useState('#00D9C8');
  const [newColorName, setNewColorName] = useState('');
  const [styleStrength, setStyleStrength] = useState('exakt');
  const [styleStrengthSaving, setStyleStrengthSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/brand-dna`, { headers: getAuthHeader() })
      .then(r => {
        // Neu: Farben aus strukturiertem Objekt
        const structured = r.data.brandDNA || r.data.structured_dna;
        const structuredColors = (structured?.visual_identity?.colors || []).map(c =>
          typeof c === 'string' ? { hex: c, name: c } : { hex: c.hex, name: c.role || c.hex }
        );
        setVisual({
          primaryColors: structuredColors.length > 0 ? structuredColors : (r.data.visualIdentity?.primaryColors || []),
          visualType: r.data.visualIdentity?.visualType || '',
          mood: structured?.visual_identity?.design_elements || r.data.visualIdentity?.mood || [],
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    // Load current style_strength from brand preferences (default: exakt)
    axios.get(`${API}/brand/preferences`, { headers: getAuthHeader() })
      .then(r => {
        const s = r.data?.style_strength;
        if (s === 'locker' || s === 'treffen' || s === 'exakt') setStyleStrength(s);
      })
      .catch(() => {});
  }, []);

  const saveStyleStrength = async (next) => {
    setStyleStrength(next); // optimistic
    setStyleStrengthSaving(true);
    try {
      // Preserve existing prefs by fetching first
      const cur = await axios.get(`${API}/brand/preferences`, { headers: getAuthHeader() });
      await axios.post(`${API}/brand/preferences`, {
        content_language: cur.data?.content_language || 'de',
        image_style: cur.data?.image_style || 'illustrations',
        platform_languages: cur.data?.platform_languages || {},
        style_strength: next,
      }, { headers: getAuthHeader() });
      toast.success(t.settings.styleStrength.saved);
    } catch {
      toast.error(t.settings.styleStrength.saveError);
    } finally {
      setStyleStrengthSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/brand-dna/visual-identity`, visual, { headers: getAuthHeader() });
      toast.success(t.settings.visual.imagerySaved);
    } catch { toast.error(t.settings.visual.saveFailed); }
    finally { setSaving(false); }
  };

  if (loading) return null;

  return (
    <div className="space-y-4">
      {/* Bildstil — kombiniert Präferenz + analysierten Bildtyp */}
      <Card className="bg-[#1A2B42] border-transparent">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center gap-2"><Palette className="w-4 h-4 text-[#00D9C8]" />{t.settings.visual.imageStyle}</CardTitle>
          <CardDescription className="text-[#A0AEC0] text-sm">{t.settings.visual.imageStyleDesc}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {[
              { key: 'illustrations', label: t.settings.visual.styleIllustrations, desc: t.settings.visual.styleIllustrationsDesc, color: 'text-[#A0AEC0]', active: 'bg-purple-500/20', border: 'border-purple-500/40' },
              { key: 'ai_people', label: t.settings.visual.styleAiPeople, desc: t.settings.visual.styleAiPeopleDesc, color: 'text-blue-400', active: 'bg-blue-500/20', border: 'border-blue-500/40' },
              { key: 'photography', label: t.settings.visual.stylePhotography, desc: t.settings.visual.stylePhotographyDesc, color: 'text-[#00D9C8]', active: 'bg-amber-400/20', border: 'border-amber-400/40' },
              { key: 'mixed', label: t.settings.visual.styleMixed, desc: t.settings.visual.styleMixedDesc, color: 'text-[#00D9C8]', active: 'bg-[#00D9C8]/20', border: 'border-[#00D9C8]/40' }
            ].map(s => (
              <button key={s.key} onClick={() => onSavePreferences(null, s.key)}
                className={`py-2.5 px-4 rounded-lg text-left transition-all border ${imageStylePref === s.key ? `${s.active} ${s.color} ${s.border}` : 'bg-[#0A1628] text-[#5A6C7D] border-transparent hover:text-[#A0AEC0]'}`}
                data-testid={`settings-image-style-${s.key}`}>
                <p className="text-sm font-medium">{s.label}</p>
                <p className="text-[10px] mt-0.5 opacity-70">{s.desc}</p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Stil-Treue Slider: bestimmt wie streng gpt-image-2 den Referenzen folgt */}
      <Card className="bg-[#1A2B42] border-transparent">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center gap-2"><Palette className="w-4 h-4 text-[#00D9C8]" />{t.settings.styleStrength.title}</CardTitle>
          <CardDescription className="text-[#A0AEC0] text-sm">{t.settings.styleStrength.description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="settings-style-strength">
            {[
              { key: 'locker',  label: t.settings.styleStrength.lockerLabel,  desc: t.settings.styleStrength.lockerDesc },
              { key: 'treffen', label: t.settings.styleStrength.treffenLabel, desc: t.settings.styleStrength.treffenDesc },
              { key: 'exakt',   label: t.settings.styleStrength.exaktLabel,   desc: t.settings.styleStrength.exaktDesc },
            ].map(s => (
              <button
                key={s.key}
                type="button"
                disabled={styleStrengthSaving}
                onClick={() => saveStyleStrength(s.key)}
                className={`py-3 px-4 rounded-lg text-left transition-all border ${styleStrength === s.key ? 'bg-[#00D9C8]/15 text-[#00D9C8] border-[#00D9C8]/40' : 'bg-[#0A1628] text-[#5A6C7D] border-transparent hover:text-[#A0AEC0]'} ${styleStrengthSaving ? 'opacity-60 cursor-wait' : ''}`}
                data-testid={`settings-style-strength-${s.key}`}
                aria-pressed={styleStrength === s.key}
              >
                <p className="text-sm font-medium">{s.label}</p>
                <p className="text-[10px] mt-0.5 opacity-70 leading-tight">{s.desc}</p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Farben aus Brand DNA */}
      <Card className="bg-[#1A2B42] border-transparent">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center gap-2"><Palette className="w-4 h-4 text-[#00D9C8]" />{t.settings.visual.brandColorsAndMood}</CardTitle>
          <CardDescription className="text-[#A0AEC0] text-sm">{t.settings.visual.brandColorsDesc}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-[#5A6C7D] text-xs block mb-2">{t.settings.visual.primaryColors}</label>
            <div className="flex flex-wrap gap-2 mb-3">
              {(visual.primaryColors || []).map((color, idx) => (
                <div key={idx} className="flex items-center gap-1.5 bg-[#0A1628] px-3 py-1.5 rounded-lg group">
                  <div className="w-4 h-4 rounded border border-white/20" style={{ backgroundColor: color.hex || color }} />
                  <span className="text-xs text-white">{color.hex || color}</span>
                  {color.name && color.name !== color.hex && <span className="text-[10px] text-[#5A6C7D]">({color.name})</span>}
                  <button onClick={() => setVisual(v => ({ ...v, primaryColors: v.primaryColors.filter((_, i) => i !== idx) }))}
                    className="ml-1 text-[#5A6C7D] hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input type="color" value={newColorHex} onChange={(e) => setNewColorHex(e.target.value)} className="w-9 h-9 rounded-lg cursor-pointer border-0 bg-transparent" data-testid="brand-dna-color-picker" />
              <Input value={newColorName} onChange={(e) => setNewColorName(e.target.value)} placeholder={t.settings.visual.colorNamePlaceholder} className="bg-[#0A1628] border-transparent text-white text-sm flex-1" data-testid="brand-dna-color-name" />
              <Button onClick={() => { if (!newColorHex) return; setVisual(v => ({ ...v, primaryColors: [...(v.primaryColors || []), { hex: newColorHex, name: newColorName || newColorHex }] })); setNewColorHex('#00D9C8'); setNewColorName(''); }}
                variant="outline" size="sm" className="border-[#00D9C8]/30 text-[#00D9C8] hover:bg-[#00D9C8]/10 px-3" data-testid="add-color-btn"><Plus className="w-4 h-4" /></Button>
            </div>
          </div>
          <div>
            <label className="text-[#5A6C7D] text-xs block mb-2">{t.settings.visual.visualMood}</label>
            <TagInput tags={visual.mood || []} onChange={(v) => setVisual(vis => ({ ...vis, mood: v }))} placeholder={t.settings.visual.moodPlaceholder} color="#00D9C8" testId="brand-dna-mood-input" />
          </div>
          <Button onClick={save} disabled={saving} className="w-full bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] font-semibold" data-testid="save-visual-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}{t.settings.visual.saveColorsAndMood}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

const ALL_PLATFORMS = [
  { key: 'linkedin', label: 'LinkedIn', icon: FaLinkedin, color: '#0077B5' },
  { key: 'instagram', label: 'Instagram', icon: FaInstagram, color: '#E4405F' },
  { key: 'facebook', label: 'Facebook', icon: FaFacebook, color: '#1877F2' },
  { key: 'x', label: 'X', icon: FaTwitter, color: '#1DA1F2' },
  { key: 'pinterest', label: 'Pinterest', icon: FaPinterest, color: '#BD081C' },
  { key: 'blog', label: 'Blog', icon: FaBlog, color: '#00D9C8' },
  { key: 'newsletter', label: 'Newsletter', icon: FaEnvelope, color: '#F59E0B' },
  { key: 'medium', label: 'Medium', icon: FaMedium, color: '#A0AEC0' },
  { key: 'pressemitteilung', label: 'Press', icon: FaNewspaper, color: '#6B7280' },
  { key: 'youtube', label: 'YouTube', icon: FaYoutube, color: '#FF0000' },
  { key: 'produktbeschreibung', label: 'Produktbeschreibung', icon: FaBox, color: '#1e9ead' },
];

const BrandPlattformen = ({ getAuthHeader }) => {
  const { t, lang } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [enabledPlatforms, setEnabledPlatforms] = useState([]);
  const [customChannels, setCustomChannels] = useState([]);
  const [platformManuals, setPlatformManuals] = useState({});
  const [savingManual, setSavingManual] = useState({});
  const [platformImageAspects, setPlatformImageAspects] = useState({});
  const [savingImageAspect, setSavingImageAspect] = useState({});
  const [savingConfig, setSavingConfig] = useState(false);
  const [platformCharLimits, setPlatformCharLimits] = useState({});
  const [savingLimits, setSavingLimits] = useState(false);
  // New custom channel form
  const [showNewChannel, setShowNewChannel] = useState(false);
  const [newChannelName, setNewChannelName] = useState('');
  const [newChannelFields, setNewChannelFields] = useState([{ name: '', maxLength: 500 }]);

  // Platforms where the user may set any limit freely (no platform cap)
  const FREE_LIMIT_PLATFORMS = ['blog', 'newsletter'];

  // Default platform max lengths (must match backend PLATFORM_SETTINGS)
  const PLATFORM_DEFAULTS = {
    linkedin: 3000, instagram: 2200, facebook: 5000, x: 280,
    pinterest: 500, blog: 2800, newsletter: 600, medium: 4000,
    pressemitteilung: 5000, youtube: 450, produktbeschreibung: 6000,
  };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/settings/brand-dna`, { headers: getAuthHeader() }).catch(() => ({ data: {} })),
      axios.get(`${API}/platform-config`, { headers: getAuthHeader() }).catch(() => ({ data: {} })),
    ]).then(([dnaRes, configRes]) => {
      setPlatformManuals(dnaRes.data.platformManuals || {});
      setPlatformImageAspects(dnaRes.data.platformImageAspects || {});
      setEnabledPlatforms(configRes.data.enabled_platforms || []);
      setCustomChannels(configRes.data.custom_channels || []);
      setPlatformCharLimits(configRes.data.platform_char_limits || {});
    }).finally(() => setLoading(false));
  }, []);

  const savePlatformConfig = async (platforms, channels, charLimits) => {
    setSavingConfig(true);
    try {
      const body = {
        enabled_platforms: platforms,
        custom_channels: channels
      };
      if (charLimits !== undefined) body.platform_char_limits = charLimits;
      await axios.post(`${API}/platform-config`, body, { headers: getAuthHeader() });
      toast.success(t.settings.channels.configSavedToast);
    } catch { toast.error(t.settings.channels.saveFailedToast); }
    finally { setSavingConfig(false); }
  };

  const saveCharLimits = async () => {
    setSavingLimits(true);
    try {
      await axios.post(`${API}/platform-config`, {
        enabled_platforms: enabledPlatforms,
        custom_channels: customChannels,
        platform_char_limits: platformCharLimits
      }, { headers: getAuthHeader() });
      toast.success(t.settings.channels.limitsSavedToast);
    } catch { toast.error(t.settings.channels.saveFailedToast); }
    finally { setSavingLimits(false); }
  };

  const togglePlatform = (key) => {
    const updated = enabledPlatforms.includes(key)
      ? enabledPlatforms.filter(p => p !== key)
      : [...enabledPlatforms, key];
    setEnabledPlatforms(updated);
    savePlatformConfig(updated, customChannels);
  };

  const addCustomChannel = () => {
    if (!newChannelName.trim()) { toast.error(t.settings.channels.nameRequired); return; }
    const validFields = newChannelFields.filter(f => f.name.trim());
    if (validFields.length === 0) { toast.error(t.settings.channels.addAtLeastOneField); return; }
    const id = `custom_${Date.now()}`;
    const channel = { id, name: newChannelName.trim(), fields: validFields.map(f => ({ name: f.name.trim(), maxLength: parseInt(f.maxLength) || 500 })) };
    const updatedChannels = [...customChannels, channel];
    const updatedPlatforms = [...enabledPlatforms, id];
    setCustomChannels(updatedChannels);
    setEnabledPlatforms(updatedPlatforms);
    savePlatformConfig(updatedPlatforms, updatedChannels);
    setShowNewChannel(false);
    setNewChannelName('');
    setNewChannelFields([{ name: '', maxLength: 500 }]);
  };

  const removeCustomChannel = (id) => {
    const updatedChannels = customChannels.filter(c => c.id !== id);
    const updatedPlatforms = enabledPlatforms.filter(p => p !== id);
    setCustomChannels(updatedChannels);
    setEnabledPlatforms(updatedPlatforms);
    savePlatformConfig(updatedPlatforms, updatedChannels);
  };

  const saveManual = async (platform) => {
    setSavingManual(prev => ({ ...prev, [platform]: true }));
    try {
      await axios.put(`${API}/settings/brand-dna/platform-manual`,
        { platform, content: platformManuals[platform] || '' }, { headers: getAuthHeader() });
      toast.success(t.settings.channels.manualSavedToast);
    } catch { toast.error(t.settings.channels.saveFailedToast); }
    finally { setSavingManual(prev => ({ ...prev, [platform]: false })); }
  };

  const saveImageAspect = async (platform, aspect) => {
    // Optimistic UI update so the toggle reflects the choice immediately
    const prevValue = platformImageAspects[platform] || '';
    setPlatformImageAspects(m => ({ ...m, [platform]: aspect }));
    setSavingImageAspect(prev => ({ ...prev, [platform]: true }));
    try {
      await axios.put(`${API}/settings/brand-dna/platform-image-aspect`,
        { platform, aspect }, { headers: getAuthHeader() });
      toast.success(t.settings.channels.imageAspectSavedToast);
    } catch {
      setPlatformImageAspects(m => ({ ...m, [platform]: prevValue }));
      toast.error(t.settings.channels.saveFailedToast);
    } finally {
      setSavingImageAspect(prev => ({ ...prev, [platform]: false }));
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#00D9C8]" /></div>;

  const activePlatforms = ALL_PLATFORMS.filter(p => enabledPlatforms.includes(p.key));
  const inactivePlatforms = ALL_PLATFORMS.filter(p => !enabledPlatforms.includes(p.key));

  return (
    <div className="space-y-6">
      {/* Platform Selection Grid */}
      <Card className="bg-[#1A2B42] border-transparent">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Layers className="w-4 h-4 text-[#00D9C8]" />{t.settings.channels.activeChannels}
          </CardTitle>
          <CardDescription className="text-[#A0AEC0] text-sm">{t.settings.channels.activeChannelsDesc}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Active platforms */}
          {activePlatforms.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
              {activePlatforms.map(p => {
                const Icon = p.icon;
                return (
                  <button key={p.key} onClick={() => togglePlatform(p.key)}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#00D9C8]/10 border border-[#00D9C8]/40 text-[#00D9C8] hover:bg-[#00D9C8]/20 transition-all text-sm"
                    data-testid={`platform-toggle-${p.key}`}>
                    <Icon className="w-4 h-4" style={{ color: p.color }} />
                    <span className="truncate">{localizePlatformLabel(p, lang)}</span>
                    <Check className="w-3 h-3 ml-auto flex-shrink-0" />
                  </button>
                );
              })}
              {/* Active custom channels */}
              {customChannels.filter(c => enabledPlatforms.includes(c.id)).map(c => (
                <button key={c.id} onClick={() => togglePlatform(c.id)}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#00D9C8]/10 border border-[#00D9C8]/40 text-[#00D9C8] hover:bg-[#00D9C8]/20 transition-all text-sm"
                  data-testid={`platform-toggle-${c.id}`}>
                  <Layers className="w-4 h-4 text-[#A0AEC0]" />
                  <span className="truncate">{c.name}</span>
                  <Check className="w-3 h-3 ml-auto flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
          {activePlatforms.length === 0 && customChannels.filter(c => enabledPlatforms.includes(c.id)).length === 0 && (
            <p className="text-[#5A6C7D] text-sm text-center py-3">{t.settings.channels.noneActive}</p>
          )}

          {/* Inactive platforms */}
          {inactivePlatforms.length > 0 && (
            <>
              <div className="border-t border-[#0A1628] pt-3">
                <p className="text-xs text-[#5A6C7D] mb-2">{t.settings.channels.availableChannels}</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                  {inactivePlatforms.map(p => {
                    const Icon = p.icon;
                    return (
                      <button key={p.key} onClick={() => togglePlatform(p.key)}
                        className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#0A1628] border border-transparent text-[#5A6C7D] hover:border-[#00D9C8]/30 hover:text-[#A0AEC0] transition-all text-sm"
                        data-testid={`platform-toggle-${p.key}`}>
                        <Icon className="w-4 h-4 opacity-40" />
                        <span className="truncate">{localizePlatformLabel(p, lang)}</span>
                      </button>
                    );
                  })}
                  {/* Inactive custom channels */}
                  {customChannels.filter(c => !enabledPlatforms.includes(c.id)).map(c => (
                    <button key={c.id} onClick={() => togglePlatform(c.id)}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#0A1628] border border-transparent text-[#5A6C7D] hover:border-[#00D9C8]/30 hover:text-[#A0AEC0] transition-all text-sm"
                      data-testid={`platform-toggle-${c.id}`}>
                      <Layers className="w-4 h-4 opacity-40" />
                      <span className="truncate">{c.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Custom Channel Creator */}
      <Card className="bg-[#1A2B42] border-transparent">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <PlusCircle className="w-4 h-4 text-[#00D9C8]" />{t.settings.channels.ownChannels}
            </CardTitle>
            {!showNewChannel && (
              <Button size="sm" variant="outline" onClick={() => setShowNewChannel(true)}
                className="border-[#00D9C8]/30 text-[#00D9C8] hover:bg-[#00D9C8]/10 h-7 text-xs" data-testid="add-custom-channel-btn">
                <Plus className="w-3 h-3 mr-1" />{t.settings.channels.newChannel}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Existing custom channels */}
          {customChannels.map(ch => (
            <div key={ch.id} className={`p-3 rounded-lg border ${enabledPlatforms.includes(ch.id) ? 'bg-[#0A1628] border-[#00D9C8]/20' : 'bg-[#0A1628] border-transparent opacity-50'}`} data-testid={`custom-channel-${ch.id}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-white text-sm font-medium flex items-center gap-2"><Layers className="w-3.5 h-3.5 text-[#A0AEC0]" />{ch.name}</span>
                <button onClick={() => removeCustomChannel(ch.id)} className="text-[#5A6C7D] hover:text-red-400 transition-colors" data-testid={`remove-custom-${ch.id}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {ch.fields.map((f, i) => (
                  <Badge key={i} className="bg-[#1A2B42] text-[#A0AEC0] text-[10px] border-transparent">{f.name} ({f.maxLength})</Badge>
                ))}
              </div>
            </div>
          ))}

          {/* New channel form */}
          {showNewChannel && (
            <div className="p-4 rounded-lg bg-[#0A1628] border border-[#00D9C8]/20 space-y-3" data-testid="new-channel-form">
              <div>
                <label className="text-xs text-[#5A6C7D] mb-1 block">{t.settings.channels.channelName}</label>
                <input value={newChannelName} onChange={e => setNewChannelName(e.target.value)}
                  placeholder={t.settings.channels.channelNamePh}
                  className="w-full bg-[#1A2B42] text-white text-sm border border-[#1A2B42] focus:border-[#00D9C8]/40 rounded-lg px-3 py-2 outline-none" data-testid="new-channel-name" />
              </div>
              <div>
                <label className="text-xs text-[#5A6C7D] mb-1 block">{t.settings.channels.fieldsLabel}</label>
                <div className="space-y-2">
                  {newChannelFields.map((field, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input value={field.name} onChange={e => { const f = [...newChannelFields]; f[i].name = e.target.value; setNewChannelFields(f); }}
                        placeholder={t.settings.channels.fieldNamePh}
                        className="flex-1 min-w-0 bg-[#1A2B42] text-white text-sm border border-[#1A2B42] focus:border-[#00D9C8]/40 rounded-lg px-3 py-1.5 outline-none" data-testid={`new-field-name-${i}`} />
                      {newChannelFields.length > 1 && (
                        <button onClick={() => setNewChannelFields(f => f.filter((_, j) => j !== i))} className="text-[#5A6C7D] hover:text-red-400 shrink-0 p-1" data-testid={`remove-field-${i}`}>
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button onClick={() => setNewChannelFields(f => [...f, { name: '', maxLength: 500 }])}
                    className="text-xs text-[#00D9C8] hover:text-[#00D9C8]/80 flex items-center gap-1" data-testid="add-field-btn">
                    <Plus className="w-3 h-3" />{t.settings.channels.addField}
                  </button>
                </div>
              </div>
              {/* Max length per field — separate section below +Add field */}
              <div className="space-y-2 pt-1">
                {newChannelFields.map((field, i) => (
                  <div key={`max-${i}`} className="flex items-center gap-2">
                    <span className="text-xs text-[#5A6C7D] shrink-0 truncate max-w-[50%]">
                      {t.settings.channels.max}{field.name ? ` ${field.name}` : ''}
                    </span>
                    <input
                      value={field.maxLength}
                      onChange={e => { const f = [...newChannelFields]; f[i].maxLength = e.target.value; setNewChannelFields(f); }}
                      type="number" min="1"
                      className="w-24 bg-[#1A2B42] text-white text-sm border border-[#1A2B42] focus:border-[#00D9C8]/40 rounded-lg px-2 py-1.5 outline-none"
                      data-testid={`new-field-limit-${i}`}
                    />
                  </div>
                ))}
              </div>
              <div className="flex gap-2 pt-1">
                <Button size="sm" onClick={addCustomChannel} className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] h-7 text-xs px-4" data-testid="save-custom-channel-btn">{t.settings.channels.createChannel}</Button>
                <Button size="sm" variant="outline" onClick={() => { setShowNewChannel(false); setNewChannelName(''); setNewChannelFields([{ name: '', maxLength: 500 }]); }}
                  className="border-[#5A6C7D]/30 text-[#5A6C7D] h-7 text-xs">{t.settings.channels.cancel}</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Character Limits per Platform */}
      {activePlatforms.length > 0 && (
        <Card className="bg-[#1A2B42] border-transparent">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Type className="w-4 h-4 text-[#00D9C8]" />{t.settings.channels.characterLimits}
            </CardTitle>
            <CardDescription className="text-[#A0AEC0] text-sm">
              {t.settings.channels.characterLimitsDesc}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Header row - desktop only */}
            <div className="hidden md:flex items-center gap-3 px-3 pb-1 border-b border-[#0A1628]">
              <span className="w-4" />
              <span className="text-[10px] text-[#5A6C7D] uppercase tracking-wider w-28">{t.settings.channels.platform}</span>
              <span className="text-[10px] text-[#5A6C7D] uppercase tracking-wider w-24 text-center">{t.settings.channels.words}</span>
              <span className="text-[10px] text-[#5A6C7D] w-4 text-center">&harr;</span>
              <span className="text-[10px] text-[#5A6C7D] uppercase tracking-wider w-24 text-center">{t.settings.channels.characters}</span>
              <span className="text-[10px] text-[#5A6C7D] flex-1">{t.settings.channels.limit}</span>
            </div>
            <div className="grid gap-2">
              {activePlatforms.map(p => {
                const Icon = p.icon;
                const defaultMax = PLATFORM_DEFAULTS[p.key] || 5000;
                const isFree = FREE_LIMIT_PLATFORMS.includes(p.key);
                const currentVal = platformCharLimits[p.key] ?? '';
                const CHARS_PER_WORD = 6; // German average ~6 chars/word
                const wordVal = currentVal ? Math.round(currentVal / CHARS_PER_WORD) : '';

                const wordInput = (
                  <input
                    type="number"
                    min="1"
                    placeholder={String(Math.round(defaultMax / CHARS_PER_WORD))}
                    value={wordVal}
                    onChange={(e) => {
                      const words = e.target.value;
                      if (!words) {
                        setPlatformCharLimits(prev => { const n = { ...prev }; delete n[p.key]; return n; });
                        return;
                      }
                      let chars = parseInt(words) * CHARS_PER_WORD;
                      if (!isFree && chars > defaultMax) chars = defaultMax;
                      setPlatformCharLimits(prev => ({ ...prev, [p.key]: chars }));
                    }}
                    className="w-full md:w-24 bg-[#1A2B42] text-white text-sm border border-[#1A2B42] focus:border-[#00D9C8]/40 rounded-lg px-2 py-1.5 outline-none text-center"
                    data-testid={`word-limit-input-${p.key}`}
                  />
                );
                const charInput = (
                  <input
                    type="number"
                    min="1"
                    max={isFree ? undefined : defaultMax}
                    placeholder={String(defaultMax)}
                    value={currentVal}
                    onChange={(e) => {
                      let val = e.target.value;
                      if (!val) {
                        setPlatformCharLimits(prev => { const n = { ...prev }; delete n[p.key]; return n; });
                        return;
                      }
                      if (!isFree && parseInt(val) > defaultMax) val = String(defaultMax);
                      setPlatformCharLimits(prev => ({ ...prev, [p.key]: parseInt(val) }));
                    }}
                    className="w-full md:w-24 bg-[#1A2B42] text-white text-sm border border-[#1A2B42] focus:border-[#00D9C8]/40 rounded-lg px-2 py-1.5 outline-none text-center"
                    data-testid={`char-limit-input-${p.key}`}
                  />
                );
                const resetBtn = currentVal ? (
                  <button
                    onClick={() => setPlatformCharLimits(prev => { const n = { ...prev }; delete n[p.key]; return n; })}
                    className="text-[#5A6C7D] hover:text-red-400 transition-colors flex-shrink-0"
                    title={t.settings.channels.resetTitle}
                    data-testid={`reset-limit-${p.key}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                ) : null;

                return (
                  <div key={p.key} data-testid={`char-limit-${p.key}`}>
                    {/* Mobile card layout (< md) */}
                    <div className="md:hidden flex flex-col gap-2 bg-[#0A1628] rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 flex-shrink-0" style={{ color: p.color }} />
                        <span className="text-sm text-white flex-1 truncate">{localizePlatformLabel(p, lang)}</span>
                        <span className="text-[10px] text-[#5A6C7D] flex-shrink-0">
                          {isFree ? t.settings.channels.free : `max ${defaultMax.toLocaleString()}`}
                        </span>
                        {resetBtn}
                      </div>
                      <div className="flex items-end gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] text-[#5A6C7D] uppercase tracking-wider mb-1">{t.settings.channels.words}</p>
                          {wordInput}
                        </div>
                        <span className="text-[#5A6C7D] text-xs pb-2">&harr;</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] text-[#5A6C7D] uppercase tracking-wider mb-1">{t.settings.channels.characters}</p>
                          {charInput}
                        </div>
                      </div>
                    </div>
                    {/* Desktop row layout (>= md) - unchanged */}
                    <div className="hidden md:flex items-center gap-3 bg-[#0A1628] rounded-lg px-3 py-2">
                      <Icon className="w-4 h-4 flex-shrink-0" style={{ color: p.color }} />
                      <span className="text-sm text-white w-28 flex-shrink-0 truncate">{localizePlatformLabel(p, lang)}</span>
                      {wordInput}
                      <span className="text-[#5A6C7D] text-xs w-4 text-center">=</span>
                      {charInput}
                      <span className="text-[10px] text-[#5A6C7D] flex-1">
                        {isFree ? t.settings.channels.free : `max ${defaultMax.toLocaleString()}`}
                      </span>
                      {resetBtn}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-[#5A6C7D]">{t.settings.channels.charsPerWordNote}</span>
              <Button size="sm" onClick={saveCharLimits} disabled={savingLimits}
                className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] h-7 text-xs px-4" data-testid="save-char-limits-btn">
                {savingLimits ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                {t.settings.channels.saveLimits}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Platform-specific Style Manuals */}
      <div className="space-y-2">
        <p className="text-xs text-[#5A6C7D] uppercase tracking-wider">{t.settings.channels.styleManualsPerPlatform}</p>
        {/* Active platforms first */}
        {ALL_PLATFORMS.filter(p => enabledPlatforms.includes(p.key)).map(p => {
          const Icon = p.icon;
          const platLabel = localizePlatformLabel(p, lang);
          return (
            <CollapsibleSection key={p.key} title={platLabel} icon={() => <Icon className="w-4 h-4" style={{ color: p.color }} />} iconColor={p.color} badge={platformManuals[p.key] ? `${platformManuals[p.key].length} ${t.settings.channels.charsBadge}` : t.settings.channels.empty}>
              <div className="space-y-3 mt-3">
                {p.key === 'linkedin' && (
                  <div className="bg-[#0A1628] rounded-lg p-3" data-testid="linkedin-image-aspect">
                    <p className="text-xs text-white font-medium mb-0.5">{t.settings.channels.imageFormatTitle}</p>
                    <p className="text-[11px] text-[#5A6C7D] mb-2.5">{t.settings.channels.imageFormatDesc}</p>
                    <div className="flex rounded-md border border-[#1A2B42] overflow-hidden max-w-xs">
                      {[
                        { key: '', label: t.settings.channels.imageFormatDefault, sub: '1.91:1' },
                        { key: '4:5', label: t.settings.channels.imageFormatPortrait, sub: '4:5' },
                      ].map(opt => {
                        const active = (platformImageAspects[p.key] || '') === opt.key;
                        return (
                          <button
                            key={opt.key || 'default'}
                            type="button"
                            disabled={savingImageAspect[p.key]}
                            onClick={() => saveImageAspect(p.key, opt.key)}
                            className={`flex-1 py-2 px-3 text-xs font-medium transition-all disabled:opacity-60 ${
                              active
                                ? 'bg-[#00D9C8] text-[#0A1628]'
                                : 'bg-[#1A2B42]/30 text-[#A0AEC0] hover:text-white'
                            }`}
                            data-testid={`linkedin-aspect-${opt.key || 'default'}`}
                          >
                            {opt.label} <span className="opacity-70">({opt.sub})</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <Textarea value={platformManuals[p.key] || ''} onChange={(e) => setPlatformManuals(m => ({ ...m, [p.key]: e.target.value }))}
                  placeholder={t.settings.channels.styleManualPh.replace('{name}', platLabel)}
                  className="bg-[#0A1628] border-transparent text-white text-sm min-h-[120px]" data-testid={`manual-${p.key}`} />
                <Button size="sm" onClick={() => saveManual(p.key)} disabled={savingManual[p.key]}
                  className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] h-7 text-xs px-3" data-testid={`save-manual-${p.key}`}>
                  {savingManual[p.key] ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}{t.common.save}
                </Button>
              </div>
            </CollapsibleSection>
          );
        })}
        {/* Custom channels manuals */}
        {customChannels.filter(c => enabledPlatforms.includes(c.id)).map(ch => (
          <CollapsibleSection key={ch.id} title={ch.name} icon={Layers} iconColor="#A0AEC0" badge={platformManuals[ch.id] ? `${platformManuals[ch.id].length} ${t.settings.channels.charsBadge}` : t.settings.channels.empty}>
            <div className="space-y-3 mt-3">
              <Textarea value={platformManuals[ch.id] || ''} onChange={(e) => setPlatformManuals(m => ({ ...m, [ch.id]: e.target.value }))}
                placeholder={t.settings.channels.customStyleManualPh.replace('{name}', ch.name)}
                className="bg-[#0A1628] border-transparent text-white text-sm min-h-[120px]" data-testid={`manual-${ch.id}`} />
              <Button size="sm" onClick={() => saveManual(ch.id)} disabled={savingManual[ch.id]}
                className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] h-7 text-xs px-3" data-testid={`save-manual-${ch.id}`}>
                {savingManual[ch.id] ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}{t.common.save}
              </Button>
            </div>
          </CollapsibleSection>
        ))}
        {/* Inactive platforms - greyed out */}
        {ALL_PLATFORMS.filter(p => !enabledPlatforms.includes(p.key)).map(p => {
          const Icon = p.icon;
          return (
            <div key={p.key} className="opacity-40 pointer-events-none">
              <CollapsibleSection title={localizePlatformLabel(p, lang)} icon={() => <Icon className="w-4 h-4" style={{ color: p.color }} />} iconColor={p.color} badge={t.settings.channels.notActive}>
                <div className="mt-3">
                  <p className="text-[#5A6C7D] text-sm">{t.settings.channels.activateAbove}</p>
                </div>
              </CollapsibleSection>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const UploadedFilesList = ({ files, onDelete }) => {
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState(false);
  return (
    <div>
      <button onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-[#A0AEC0] text-sm hover:text-white transition-colors mb-2">
        <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        {t.settings.inspirations.showPreviousUploads.replace('{n}', files.length)}
      </button>
      {expanded && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
          {files.map((file) => (
            <div key={file.id} className="relative group">
              <img src={file.url} alt={file.original_name} className="w-full h-20 object-cover rounded-lg" />
              <button onClick={() => onDelete(file.id)} className="absolute top-1 right-1 p-1 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CompanyEditor = ({ companyProfile, setCompanyProfile, getAuthHeader }) => {
  const { t } = useLanguage();
  const raw = companyProfile?.raw_json || {};
  const [companyName, setCompanyName] = useState(raw.companyName || '');
  const [industry, setIndustry] = useState(raw.industry || '');
  const [coreActivities, setCoreActivities] = useState(raw.coreActivities || '');
  const [targetAudience, setTargetAudience] = useState(raw.targetAudience || '');
  const [usps, setUsps] = useState(raw.usps || []);
  const [newUsp, setNewUsp] = useState('');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const r = companyProfile?.raw_json || {};
    setCompanyName(r.companyName || '');
    setIndustry(r.industry || '');
    setCoreActivities(r.coreActivities || '');
    setTargetAudience(r.targetAudience || '');
    setUsps(r.usps || []);
    setDirty(false);
  }, [companyProfile]);

  const markDirty = (setter) => (val) => { setter(val); setDirty(true); };

  const addUsp = () => {
    if (!newUsp.trim()) return;
    setUsps(prev => [...prev, newUsp.trim()]);
    setNewUsp('');
    setDirty(true);
  };

  const removeUsp = (index) => {
    setUsps(prev => prev.filter((_, i) => i !== index));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings/company-profile`, {
        fields: { companyName, industry, coreActivities, targetAudience, usps }
      }, { headers: getAuthHeader() });
      setCompanyProfile(res.data);
      setDirty(false);
      toast.success('Unternehmensdaten gespeichert');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  const hasData = companyName || industry || usps.length > 0;

  if (!hasData && !companyProfile?.raw_json) {
    return (
      <div className="text-center py-8 text-[#5A6C7D]">
        <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>Noch keine Analyse vorhanden</p>
        <p className="text-sm">{t.settings.company.urlAnalyzeDesc}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 pt-4 border-t border-[#0A1628]">
      <div className="flex items-center justify-between">
        <h3 className="text-white font-medium">{t.settings.company.companyDataTitle}</h3>
        {dirty && (
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] h-8 px-4 text-xs"
            data-testid="save-company-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
            Speichern
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs text-[#A0AEC0]">{t.settings.company.name}</Label>
          <Input
            value={companyName}
            onChange={(e) => markDirty(setCompanyName)(e.target.value)}
            placeholder={t.settings.company.name}
            className="bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white"
            data-testid="company-name-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-[#A0AEC0]">{t.settings.company.industry}</Label>
          <Textarea
            value={industry}
            onChange={(e) => markDirty(setIndustry)(e.target.value)}
            placeholder={t.settings.company.industryPlaceholder}
            rows={3}
            className="bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white resize-none overflow-y-auto"
            data-testid="company-industry-input"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs text-[#A0AEC0]">{t.settings.company.activities}</Label>
        <Textarea
          value={coreActivities}
          onChange={(e) => markDirty(setCoreActivities)(e.target.value)}
          placeholder={t.settings.company.coreBusinessPlaceholder}
          rows={3}
          className="bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white min-h-[90px] max-h-[240px] md:min-h-[60px] md:max-h-[none] resize-none overflow-y-auto"
          data-testid="company-activities-input"
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs text-[#A0AEC0]">{t.settings.company.targetAudience}</Label>
        {/* Mobile: 3-line Textarea (internal scroll for longer text) */}
        <Textarea
          value={targetAudience}
          onChange={(e) => markDirty(setTargetAudience)(e.target.value)}
          placeholder={t.settings.company.audiencePlaceholder}
          rows={3}
          className="md:hidden bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white min-h-[80px] max-h-[200px] resize-none overflow-y-auto"
          data-testid="company-audience-input"
        />
        {/* Desktop: unchanged single-line Input */}
        <Input
          value={targetAudience}
          onChange={(e) => markDirty(setTargetAudience)(e.target.value)}
          placeholder={t.settings.company.audiencePlaceholder}
          className="hidden md:block bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white"
          data-testid="company-audience-input-desktop"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs text-[#A0AEC0]">{t.settings.company.usps}</Label>
        <div className="flex flex-wrap gap-2">
          {usps.map((usp, i) => (
            <span key={i} className="flex items-center gap-1.5 px-2.5 py-1 bg-[#00D9C8]/10 text-[#00D9C8] text-xs rounded-md group">
              {usp}
              <button onClick={() => removeUsp(i)} className="opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-400">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            value={newUsp}
            onChange={(e) => setNewUsp(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addUsp())}
            placeholder={t.settings.company.addUspPlaceholder}
            className="bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white text-sm"
            data-testid="company-usp-input"
          />
          <Button onClick={addUsp} variant="ghost" className="text-[#00D9C8] hover:bg-[#00D9C8]/10 h-10 px-3" data-testid="add-usp-btn">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {dirty && (
        <div className="flex justify-end pt-2">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628]"
            data-testid="save-company-btn-bottom"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Unternehmensdaten speichern
          </Button>
        </div>
      )}
    </div>
  );
};


// LogoPlacementEditor now lives in components/LogoPlacementEditor.jsx (imported
// at the top of this file) so the new BrandDnaView reuses the SAME component.

// ── Main Settings Component ───────────────────────────────────────────────────

const Settings = () => {
  const { user, getAuthHeader, activeProjectId, isProjectReady } = useAuth();
  const { t, lang } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'profile';
  const [activeTab, setActiveTab] = useState(initialTab);
  const location = useLocation();
  const { dnaName } = useActiveProjectType();      // „Personal DNA“ bei Personen-Projekten, sonst „Brand DNA“
  const [markeSubPage, setMarkeSubPage] = useState(searchParams.get('sub') || 'uebersicht');
  // Deep-Link auf eine Unterseite der Marke (z. B. ?tab=marke&sub=uebersicht); location.key ändert sich auch bei gleicher URL
  useEffect(() => {
    const sub = new URLSearchParams(location.search).get('sub');
    if (sub) setMarkeSubPage(sub);
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps
  const [markeMobileMenuOpen, setMarkeMobileMenuOpen] = useState(false); // mobile-only: Brand DNA subsection dropdown
  const tabsListRef = useRef(null);

  // Close mobile Brand DNA dropdown when switching away from marke tab
  useEffect(() => {
    if (activeTab !== 'marke') setMarkeMobileMenuOpen(false);
  }, [activeTab]);

  // Mobile: auto-scroll the active tab into view inside the horizontally scrollable TabsList
  useEffect(() => {
    if (!tabsListRef.current) return;
    if (typeof window === 'undefined') return;
    if (!window.matchMedia('(max-width: 767px)').matches) return;
    const active = tabsListRef.current.querySelector('[data-state="active"]');
    if (active && typeof active.scrollIntoView === 'function') {
      active.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [activeTab]);

  // Keep the URL in sync when the user switches tabs so the tab is
  // deep-linkable (e.g. from the Distribution "connect channels" CTA).
  useEffect(() => {
    const current = searchParams.get('tab');
    if (activeTab && activeTab !== current) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', activeTab);
      setSearchParams(next, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Profile
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [passwordSet, setPasswordSet] = useState(true);

  // Company
  const [companyProfile, setCompanyProfile] = useState(null);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [analyzingCompany, setAnalyzingCompany] = useState(false);
  const [loadingCompany, setLoadingCompany] = useState(true);

  // Presence (for screenshot uploads)
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [analyzingPresence, setAnalyzingPresence] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  // Integrations
  const [linkedinStatus, setLinkedinStatus] = useState({ connected: false, linkedin_name: '', linkedin_email: '' });
  const [loadingLinkedin, setLoadingLinkedin] = useState(true);
  const [connectingLinkedin, setConnectingLinkedin] = useState(false);
  const [pinterestStatus, setPinterestStatus] = useState({ connected: false, pinterest_name: '', pinterest_username: '', boards: [] });
  const [loadingPinterest, setLoadingPinterest] = useState(true);
  const [connectingPinterest, setConnectingPinterest] = useState(false);

  // Logo Overlay
  const [overlaySettings, setOverlaySettings] = useState({
    overlay_enabled: false,
    logo_url: null,
    logo_position: 'bottom-right',
    logo_size: 'medium',
    logo_x_percent: 0.82,
    logo_y_percent: 0.82,
    logo_size_percent: 0.15,
  });

  // Outstand-backed social platforms (LinkedIn, Instagram, Facebook, X, Pinterest, YouTube)
  const [connectedPlatforms, setConnectedPlatforms] = useState([]);
  const [connectingPlatform, setConnectingPlatform] = useState(null);
  const [disconnectingPlatform, setDisconnectingPlatform] = useState(null);
  const [disconnectConfirm, setDisconnectConfirm] = useState(null); // {platform, pendingCount} or null
  const [integrationsUnlocked, setIntegrationsUnlocked] = useState(false);
  const [intPwInput, setIntPwInput] = useState('');
  const [intPwError, setIntPwError] = useState(false);
  const INT_PW = 'wergsd6249rcsxsj';

  // Platforms whose accounts need special preconditions (business accounts etc.).
  const PLATFORM_PREREQS = t.settings.integrations.prereqs;

  // Brand data
  const [inspirations, setInspirations] = useState([]);
  const [newInspiration, setNewInspiration] = useState({ type: 'note', platform: '', text: '', what_i_like: '' });
  const [reAnalyzing, setReAnalyzing] = useState(false);
  const [showPastInspirations, setShowPastInspirations] = useState(false);
  const [uploadingScreenshot, setUploadingScreenshot] = useState(false);

  // Brand media — managed by the shared <BrandMediaManager/> component.

  // Hauptprodukt
  const [hauptprodukt, setHauptprodukt] = useState(null);
  const [uploadingHauptprodukt, setUploadingHauptprodukt] = useState(false);
  const [hpName, setHpName] = useState('');
  const [hpDescription, setHpDescription] = useState('');
  const [hpType, setHpType] = useState('product');

  // Preferences
  const [contentLanguage, setContentLanguage] = useState('de');
  const [imageStylePref, setImageStylePref] = useState('illustrations');
  const [platformLanguages, setPlatformLanguages] = useState({});
  const [savingPreferences, setSavingPreferences] = useState(false);

  // User data
  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
      // `password_set` is undefined for older session/me payloads → treat
      // as already set (no behaviour change for existing flows).
      setPasswordSet(user.password_set !== false);
    }
  }, [user]);

  const loadCompanyProfile = useCallback(async () => {
    try {
      setLoadingCompany(true);
      const response = await axios.get(`${API}/settings/company-profile`, { headers: getAuthHeader() });
      setCompanyProfile(response.data);
      setWebsiteUrl(response.data?.website_url || '');
    } catch (err) { console.error('Error loading company profile:', err); }
    finally { setLoadingCompany(false); }
  }, [getAuthHeader]);

  const loadLinkedinStatus = async () => {
    try {
      setLoadingLinkedin(true);
      const r = await axios.get(`${API}/linkedin/status`, { headers: getAuthHeader() });
      setLinkedinStatus(r.data);
    } catch { } finally { setLoadingLinkedin(false); }
  };

  const loadPinterestStatus = async () => {
    try {
      setLoadingPinterest(true);
      const r = await axios.get(`${API}/pinterest/status`, { headers: getAuthHeader() });
      setPinterestStatus(r.data);
    } catch { } finally { setLoadingPinterest(false); }
  };

  const loadOverlaySettings = async () => {
    try {
      const r = await axios.get(`${API}/settings/overlay`, { headers: getAuthHeader() });
      if (r.data) setOverlaySettings(prev => ({ ...prev, ...r.data }));
    } catch {}
  };

  const saveOverlaySettings = async (next) => {
    try {
      await axios.post(`${API}/settings/overlay`, next, { headers: { ...getAuthHeader(), 'Content-Type': 'application/json' } });
    } catch (e) { console.error('Overlay save failed:', e); }
  };

  const loadIntegrationsStatus = async () => {
    try {
      const r = await axios.get(`${API}/integrations/status`, { headers: getAuthHeader() });
      setConnectedPlatforms(Array.isArray(r.data?.connected) ? r.data.connected : []);
    } catch {
      // Endpoint may be unavailable or user has no Outstand account yet — keep silent.
    }
  };

  const loadBrandData = async () => {
    try {
      const [inspRes, hpRes, prefRes] = await Promise.all([
        axios.get(`${API}/brand/inspirations`, { headers: getAuthHeader() }).catch(() => ({ data: { inspirations: [] } })),
        axios.get(`${API}/brand/hauptprodukt`, { headers: getAuthHeader() }).catch(() => ({ data: null })),
        axios.get(`${API}/brand/preferences`, { headers: getAuthHeader() }).catch(() => ({ data: {} })),
      ]);
      setInspirations(inspRes.data.inspirations || []);
      setHauptprodukt(hpRes.data?.hauptprodukt || null);
      if (prefRes.data.content_language) setContentLanguage(prefRes.data.content_language);
      if (prefRes.data.image_style) setImageStylePref(prefRes.data.image_style);
      if (prefRes.data.platform_languages) setPlatformLanguages(prefRes.data.platform_languages);
    } catch (err) { console.error('Error loading brand data:', err); }
  };

  const loadPresenceFiles = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/settings/presence-profile`, { headers: getAuthHeader() });
      setUploadedFiles(r.data.files || []);
    } catch { }
  }, [getAuthHeader]);

  useEffect(() => {
    loadCompanyProfile();
    loadLinkedinStatus();
    loadPinterestStatus();
    loadIntegrationsStatus();
    loadBrandData();
    loadPresenceFiles();
    loadOverlaySettings();
    const params = new URLSearchParams(window.location.search);
    const linkedinToken = params.get('linkedin_token');
    if (linkedinToken) { setActiveTab('integrations'); loadLinkedinStatus(); }
    const requestedTab = params.get('tab');
    const VALID_TABS = ['profile', 'company', 'marke', 'integrations', 'abrechnung'];
    if (requestedTab && VALID_TABS.includes(requestedTab)) {
      setActiveTab(requestedTab);
    }

    // Reusable auto-map helper — retries for up to ~30 s while PostPeer
    // finishes listing the newly connected integration.
    const runAutoMapAndRefresh = async (platform) => {
      for (let attempt = 0; attempt < 10; attempt++) {
        try {
          const { data } = await axios.post(
            `${API}/integrations/postpeer/auto-map-latest`,
            { platform },
            { headers: getAuthHeader() },
          );
          if (data?.ok) {
            await loadIntegrationsStatus();
            return true;
          }
        } catch (e) {
          if (e?.response?.status !== 404) {
            console.warn('auto-map-latest failed:', e?.response?.data || e.message);
          }
        }
        await new Promise(r => setTimeout(r, 3000));
      }
      return false;
    };

    // Listen for postMessage from the OAuth callback tab so the UI updates
    // automatically once the user finishes connecting a social account.
    // NOTE: origin check intentionally loosened — the OAuth callback may land
    // on a different origin (production domain vs preview domain). Message
    // shape validation below is enough.
    const onMessage = async (event) => {
      if (event.data?.type !== 'OUTSTAND_CONNECTED') return;
      const { platform, status } = event.data;
      if (status === 'ok' && platform) {
        const mapped = await runAutoMapAndRefresh(platform);
        const label = platform.charAt(0).toUpperCase() + platform.slice(1);
        if (mapped) toast.success(`${label} verbunden!`);
        else toast.info(`${label}: OAuth abgeschlossen — Verknüpfung wird noch verifiziert.`);
      } else if (status === 'error') {
        toast.error('Verbindung fehlgeschlagen');
      }
      setConnectingPlatform(null);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fallback: while a "Verbinden" click is pending, poll auto-map + status
  // even if the callback tab never sends postMessage (cross-origin, popup
  // blockers, etc.). Runs for up to 60 s per connect attempt.
  useEffect(() => {
    if (!connectingPlatform) return;
    let cancelled = false;
    const start = Date.now();
    const poll = async () => {
      while (!cancelled && Date.now() - start < 60000) {
        try {
          const { data } = await axios.post(
            `${API}/integrations/postpeer/auto-map-latest`,
            { platform: connectingPlatform },
            { headers: getAuthHeader() },
          );
          if (data?.ok) {
            await loadIntegrationsStatus();
            if (!cancelled) {
              const label = connectingPlatform.charAt(0).toUpperCase() + connectingPlatform.slice(1);
              if (data.changed) toast.success(`${label} verbunden!`);
              setConnectingPlatform(null);
            }
            return;
          }
        } catch (_) { /* keep polling on 404 */ }
        await new Promise(r => setTimeout(r, 3000));
      }
      if (!cancelled) setConnectingPlatform(null);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectingPlatform]);

  // Project-scoped data must reload whenever the active project changes.
  // Wait for isProjectReady so we do not show data from a stale project
  // right after login / page refresh.
  useEffect(() => {
    if (!isProjectReady || !activeProjectId) return;
    loadCompanyProfile();
    loadBrandData();
    loadPresenceFiles();
    loadOverlaySettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProjectId, isProjectReady]);

  const handleDisconnectPlatform = async (platform, { force = false } = {}) => {
    if (!activeProjectId) { toast.error('Kein aktives Projekt'); return; }
    setDisconnectingPlatform(platform);
    try {
      await axios.delete(
        `${API}/integrations/postpeer/disconnect/project/${activeProjectId}/${platform}` + (force ? '?force=true' : ''),
        { headers: getAuthHeader() },
      );
      toast.success(`${platform} getrennt`);
      setDisconnectConfirm(null);
      await loadIntegrationsStatus();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e.message;
      if (status === 409) {
        // Extract pending count from message and ask for confirmation
        const m = (detail || '').match(/^(\d+)\s+geplante Posts/);
        const pendingCount = m ? parseInt(m[1], 10) : null;
        setDisconnectConfirm({ platform, pendingCount, message: detail });
      } else {
        toast.error(`Trennen fehlgeschlagen: ${detail}`);
      }
    } finally {
      setDisconnectingPlatform(null);
    }
  };

  const handleAddInspiration = async () => {
    if (!newInspiration.text.trim()) return;
    try {
      const res = await axios.post(`${API}/brand/inspirations`, newInspiration, { headers: getAuthHeader() });
      setInspirations(prev => [res.data.inspiration, ...prev]);
      setNewInspiration({ type: 'note', platform: '', text: '', what_i_like: '' });
      toast.success('Inspiration hinzugefügt');
    } catch { toast.error('Fehler beim Hinzufügen'); }
  };

  const handleDeleteInspiration = async (id) => {
    try {
      await axios.delete(`${API}/brand/inspirations/${id}`, { headers: getAuthHeader() });
      setInspirations(prev => prev.filter(i => i.id !== id));
      toast.success('Gelöscht');
    } catch { toast.error('Fehler beim Löschen'); }
  };

  const handleReAnalyze = async () => {
    setReAnalyzing(true);
    try {
      const inspirationPosts = inspirations.filter(i => i.text?.trim()).map(i => ({ text: i.text, image_b64: null }));
      const platformGroups = {};
      inspirations.filter(i => i.text?.trim() && i.platform).forEach(i => {
        if (!platformGroups[i.platform]) platformGroups[i.platform] = [];
        platformGroups[i.platform].push({ text: i.text, image_b64: null });
      });
      const platformsPayload = Object.keys(platformGroups).length > 0
        ? Object.fromEntries(Object.entries(platformGroups).map(([p, posts]) => [p, { posts: posts.slice(0, 5) }]))
        : { general: { posts: inspirationPosts.length > 0 ? inspirationPosts.slice(0, 10) : [{ text: 'Brand DNA Re-Analyse', image_b64: null }] } };
      await axios.post(`${API}/settings/re-analyze-platforms`, { language: 'de', platforms: platformsPayload }, { headers: getAuthHeader(), timeout: 180000 });
      toast.success('Brand DNA wurde neu analysiert!');
    } catch { toast.error('Fehler bei der Re-Analyse'); }
    finally { setReAnalyzing(false); }
  };

  const handleUploadHauptprodukt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingHauptprodukt(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', hpName || file.name);
      formData.append('description', hpDescription);
      formData.append('hp_type', hpType);
      const r = await axios.post(`${API}/brand/hauptprodukt`, formData, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
      setHauptprodukt(r.data.hauptprodukt);
      toast.success('Hauptprodukt hochgeladen');
    } catch { toast.error('Upload fehlgeschlagen'); }
    finally { setUploadingHauptprodukt(false); e.target.value = ''; }
  };

  const handleDeleteHauptprodukt = async () => {
    try {
      await axios.delete(`${API}/brand/hauptprodukt`, { headers: getAuthHeader() });
      setHauptprodukt(null);
      toast.success('Gelöscht');
    } catch { toast.error('Fehler beim Löschen'); }
  };

  const handleUploadScreenshot = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingScreenshot(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await axios.post(`${API}/brand/inspirations/upload-screenshot`, formData,
        { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
      setInspirations(prev => [r.data.inspiration, ...prev]);
      toast.success('Screenshot analysiert — Plattform automatisch erkannt');
    } catch { toast.error('Screenshot-Upload fehlgeschlagen'); }
    finally { setUploadingScreenshot(false); e.target.value = ''; }
  };

  const handleSavePreferences = async (lang, style) => {
    setSavingPreferences(true);
    const newLang = lang || contentLanguage;
    const newStyle = style || imageStylePref;
    if (lang) setContentLanguage(lang);
    if (style) setImageStylePref(style);
    try {
      await axios.post(`${API}/brand/preferences`, { content_language: newLang, image_style: newStyle, platform_languages: platformLanguages }, { headers: getAuthHeader() });
    } catch { toast.error('Fehler beim Speichern'); }
    finally { setSavingPreferences(false); }
  };

  const handleSavePlatformLanguages = async (updated) => {
    setPlatformLanguages(updated);
    try {
      await axios.post(`${API}/brand/preferences`, { content_language: contentLanguage, image_style: imageStylePref, platform_languages: updated }, { headers: getAuthHeader() });
    } catch { toast.error('Fehler beim Speichern'); }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const wasFirstTime = !passwordSet && !!newPassword;
      await axios.put(`${API}/settings/profile`, { name, current_password: currentPassword || undefined, new_password: newPassword || undefined }, { headers: getAuthHeader() });
      toast.success(wasFirstTime ? 'Passwort vergeben — du kannst dich ab jetzt auch klassisch anmelden' : 'Profil gespeichert');
      setCurrentPassword(''); setNewPassword('');
      // After first password creation flip UI to "change" mode without forcing reload.
      if (wasFirstTime) setPasswordSet(true);
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler beim Speichern'); }
    finally { setSavingProfile(false); }
  };

  const handleAnalyzeCompany = async () => {
    if (!websiteUrl.trim()) { toast.error('Bitte gib eine Website-URL ein'); return; }
    setAnalyzingCompany(true);
    try {
      const r = await axios.post(`${API}/settings/analyze-company`, { url: websiteUrl }, { headers: getAuthHeader(), timeout: 60000 });

      // Sync response: profile with raw_json present
      if (r.data.raw_json) {
        setCompanyProfile(r.data);
        toast.success('Website-Analyse abgeschlossen');
        return;
      }

      // Background job — poll
      if (r.data.job_id && r.data.status === 'processing') {
        const jobId = r.data.job_id;
        for (let i = 0; i < 90; i++) {
          await new Promise(res => setTimeout(res, 2000));
          try {
            const s = await axios.get(`${API}/settings/analyze-company/${jobId}`, { headers: getAuthHeader(), timeout: 15000 });
            if (s.data.status === 'completed' && s.data.raw_json) {
              setCompanyProfile(s.data);
              toast.success('Website-Analyse abgeschlossen');
              return;
            }
            if (s.data.status === 'error') {
              toast.error(s.data.error || 'Analyse fehlgeschlagen');
              return;
            }
          } catch (_) { /* transient — keep polling */ }
        }
        toast.error('Analyse dauert zu lange. Bitte erneut versuchen.');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Analyse fehlgeschlagen'); }
    finally { setAnalyzingCompany(false); }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploadingFile(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        const r = await axios.post(`${API}/settings/upload-presence-file`, formData, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
        setUploadedFiles(prev => [...prev, r.data]);
      }
      toast.success(`${files.length} Datei(en) hochgeladen`);
    } catch { toast.error('Upload fehlgeschlagen'); }
    finally { setUploadingFile(false); e.target.value = ''; }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await axios.delete(`${API}/settings/presence-file/${fileId}`, { headers: getAuthHeader() });
      setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
      toast.success('Datei gelöscht');
    } catch { toast.error('Löschen fehlgeschlagen'); }
  };

  const handleAnalyzePresence = async () => {
    if (uploadedFiles.length === 0) { toast.error('Bitte lade zuerst Screenshots hoch'); return; }
    setAnalyzingPresence(true);
    try {
      const inspirationPosts = inspirations.filter(i => i.text?.trim()).map(i => ({ text: i.text, image_b64: null }));
      await axios.post(`${API}/settings/re-analyze-platforms`, {
        language: 'de',
        platforms: { general: { posts: inspirationPosts.length > 0 ? inspirationPosts.slice(0, 10) : [{ text: 'Markenanalyse', image_b64: null }] } }
      }, { headers: getAuthHeader(), timeout: 180000 });
      toast.success('Brand-Analyse abgeschlossen');
    } catch (err) { toast.error(err.response?.data?.detail || 'Analyse fehlgeschlagen'); }
    finally { setAnalyzingPresence(false); }
  };

  const handleLinkedinConnect = async () => {
    setConnectingLinkedin(true);
    try {
      const r = await axios.get(`${API}/linkedin/auth-url`, { headers: getAuthHeader() });
      window.location.href = r.data.auth_url;
    } catch { toast.error('Fehler beim Verbinden'); setConnectingLinkedin(false); }
  };

  const handleLinkedinDisconnect = async () => {
    try {
      await axios.post(`${API}/linkedin/disconnect`, {}, { headers: getAuthHeader() });
      setLinkedinStatus({ connected: false, linkedin_name: '', linkedin_email: '' });
      toast.success('LinkedIn getrennt');
    } catch { toast.error('Fehler beim Trennen'); }
  };

  const handlePinterestConnect = async () => {
    setConnectingPinterest(true);
    try {
      const r = await axios.get(`${API}/pinterest/auth-url`, { headers: getAuthHeader() });
      window.location.href = r.data.auth_url;
    } catch { toast.error('Fehler beim Verbinden'); setConnectingPinterest(false); }
  };

  const handlePinterestDisconnect = async () => {
    try {
      await axios.post(`${API}/pinterest/disconnect`, {}, { headers: getAuthHeader() });
      setPinterestStatus({ connected: false });
      toast.success('Pinterest getrennt');
    } catch { toast.error('Fehler beim Trennen'); }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="mx-auto min-w-0 max-w-full md:max-w-4xl" data-testid="settings-page">
      <div className="mb-6">
        <h1 className="text-2xl lg:text-4xl font-black tracking-tight text-white mb-2">{t.settings.title}</h1>
        <p className="text-sm lg:text-base text-[#A0AEC0]">{t.settings.subtitle}</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList
          ref={tabsListRef}
          className="flex md:grid w-full md:grid-cols-5 justify-start md:justify-normal gap-1 md:gap-0 overflow-x-auto md:overflow-visible bg-[#1A2B42] mb-6 h-auto md:h-9 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          <TabsTrigger value="profile" className="flex-shrink-0 whitespace-nowrap data-[state=active]:bg-[#00D9C8] data-[state=active]:text-[#0A1628] text-sm px-3">
            <User className="w-4 h-4 mr-1 md:mr-2 hidden md:inline-block" />{t.nav.profile}
          </TabsTrigger>
          <TabsTrigger value="company" className="flex-shrink-0 whitespace-nowrap data-[state=active]:bg-[#00D9C8] data-[state=active]:text-[#0A1628] text-sm px-3">
            <Building2 className="w-4 h-4 mr-1 md:mr-2 hidden md:inline-block" />{t.settings.tabs.company}
          </TabsTrigger>
          <Popover
            open={markeMobileMenuOpen}
            onOpenChange={(o) => {
              // Only allow the popover to open on mobile
              if (o && !(typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches)) return;
              setMarkeMobileMenuOpen(o);
            }}
          >
            <PopoverTrigger asChild>
              <TabsTrigger
                value="marke"
                onClick={() => {
                  // Mobile-only: tapping Brand DNA also toggles the anchored subsection dropdown
                  if (typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches) {
                    setMarkeMobileMenuOpen(prev => activeTab === 'marke' ? !prev : true);
                  }
                }}
                className="flex-shrink-0 whitespace-nowrap data-[state=active]:bg-[#00D9C8] data-[state=active]:text-[#0A1628] text-sm px-3"
                data-testid="marke-tab"
              >
                <Sparkles className="w-4 h-4 mr-1 md:mr-2 hidden md:inline-block" />{dnaName}
              </TabsTrigger>
            </PopoverTrigger>
            <PopoverContent
              align="center"
              sideOffset={6}
              className="md:hidden w-56 p-1 bg-[#1A2B42] border border-[#0A1628] rounded-lg"
              data-testid="marke-mobile-dropdown"
            >
              {[
                { id: 'uebersicht',   icon: BookOpen, label: dnaName,      color: '#00D9C8' },
                { id: 'kanaele',      icon: Layers,   label: lang === 'de' ? 'Kanäle'         : 'Channels',       color: '#38BDF8' },
                { id: 'strategie',    icon: Target,   label: lang === 'de' ? 'Strategie'      : 'Strategy',       color: '#A78BFA' },
                { id: 'wissen',       icon: Library,  label: lang === 'de' ? 'Wissen'         : 'Knowledge',      color: '#FBBF24' },
              ].map(({ id, icon: Icon, label, color }) => (
                <button
                  key={id}
                  disabled={id === 'produkte'}
                  onClick={() => {
                    setMarkeSubPage(id);
                    setMarkeMobileMenuOpen(false);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm font-medium text-left transition-colors ${id === 'produkte' ? 'opacity-40 cursor-not-allowed' : markeSubPage === id ? 'bg-[#0A1628] text-white' : 'text-[#A0AEC0] hover:bg-[#0A1628]/60 hover:text-white'}`}
                  title={id === 'produkte' ? (lang === 'de' ? 'Bald verfügbar' : 'Coming soon') : undefined}
                  data-testid={`marke-mobile-nav-${id}`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" style={{ color }} />
                  <span>{label}</span>
                </button>
              ))}
            </PopoverContent>
          </Popover>
          <TabsTrigger value="integrations" className="flex-shrink-0 whitespace-nowrap data-[state=active]:bg-[#00D9C8] data-[state=active]:text-[#0A1628] text-sm px-3" data-testid="integrations-tab">
            <Link2 className="w-4 h-4 mr-1 md:mr-2 hidden md:inline-block" />{t.settings.integrationsTab}
          </TabsTrigger>
          <TabsTrigger value="abrechnung" className="flex-shrink-0 whitespace-nowrap data-[state=active]:bg-[#00D9C8] data-[state=active]:text-[#0A1628] text-sm px-3" data-testid="abrechnung-tab">
            <Package className="w-4 h-4 mr-1 md:mr-2 hidden md:inline-block" />{t.settings.billing}
          </TabsTrigger>
        </TabsList>

        {/* ── PROFIL TAB ── */}
        <TabsContent value="profile">
          <Card className="bg-[#1A2B42] border-transparent">
            <CardHeader>
              <CardTitle className="text-white">{t.settings.personalInfo}</CardTitle>
              <CardDescription className="text-[#A0AEC0]">{t.settings.personalInfoDesc}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white">Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white" data-testid="settings-name-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-white">E-Mail</Label>
                <Input value={email} disabled className="bg-[#0A1628] border-transparent text-[#5A6C7D]" />
                <p className="text-xs text-[#5A6C7D]">{t.settings.emailImmutable}</p>
              </div>
              <div className="pt-4 border-t border-[#0A1628]">
                <h3 className="text-white font-medium mb-4" data-testid="password-section-heading">
                  {passwordSet ? t.settings.passwordChange : t.settings.passwordSet}
                </h3>
                {!passwordSet && (
                  <p className="text-xs text-[#A0AEC0] mb-4" data-testid="password-first-time-hint">
                    {t.settings.passwordFirstTimeHint}
                  </p>
                )}
                <div className="space-y-4">
                  {passwordSet && (
                    <div className="space-y-2">
                      <Label className="text-white">{t.settings.currentPassword}</Label>
                      <div className="relative">
                        <Input type={showPasswords ? 'text' : 'password'} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white pr-10" placeholder="••••••••" data-testid="current-password-input" />
                        <button type="button" onClick={() => setShowPasswords(!showPasswords)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5A6C7D] hover:text-white">
                          {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label className="text-white">{passwordSet ? t.settings.newPassword : t.settings.password}</Label>
                    <Input type={showPasswords ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white" placeholder="••••••••" data-testid="new-password-input" />
                  </div>
                </div>
              </div>
              <Button onClick={handleSaveProfile} disabled={savingProfile} className="w-full bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] font-semibold" data-testid="save-profile-btn">
                {savingProfile ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t.settings.saving}</> : <><Save className="w-4 h-4 mr-2" />{t.settings.saveChanges}</>}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── UNTERNEHMEN TAB ── */}
        <TabsContent value="company">
          <Card className="bg-[#1A2B42] border-transparent">
            <CardHeader>
              <CardTitle className="text-white">{t.settings.companyAnalysis}</CardTitle>
              <CardDescription className="text-[#A0AEC0]">{t.settings.company.companyDataDesc}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white">Website-URL</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5A6C7D]" />
                    <Input value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} placeholder="https://deine-website.de" className="pl-10 bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white" data-testid="company-url-input" />
                  </div>
                  <Button onClick={handleAnalyzeCompany} disabled={analyzingCompany} className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628]" data-testid="analyze-company-btn">
                    {analyzingCompany ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              {loadingCompany ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#00D9C8]" /></div>
              ) : (
                <CompanyEditor
                  companyProfile={companyProfile}
                  setCompanyProfile={setCompanyProfile}
                  getAuthHeader={getAuthHeader}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── BRAND DNA TAB ── */}
        <TabsContent value="marke">
          <div className="flex gap-0 min-h-[600px]">

            {/* Left Nav - Desktop only (hidden on mobile <768px) */}
            <div className="hidden md:block w-44 flex-shrink-0 pr-4">
              <div className="space-y-1 sticky top-4">
                {[
                  { id: 'uebersicht',   icon: BookOpen, label: dnaName,      color: '#00D9C8' },
                  { id: 'kanaele',      icon: Layers,   label: lang === 'de' ? 'Kanäle'         : 'Channels',       color: '#38BDF8' },
                { id: 'strategie',    icon: Target,   label: lang === 'de' ? 'Strategie'      : 'Strategy',       color: '#A78BFA' },
                { id: 'wissen',       icon: Library,  label: lang === 'de' ? 'Wissen'         : 'Knowledge',      color: '#FBBF24' },
                ].map(({ id, icon: Icon, label, color }) => (
                  <button key={id} disabled={id === 'produkte'} onClick={() => setMarkeSubPage(id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-all text-left ${id === 'produkte' ? 'opacity-40 cursor-not-allowed' : markeSubPage === id ? 'bg-[#1A2B42] text-white' : 'text-[#5A6C7D] hover:text-[#A0AEC0] hover:bg-[#1A2B42]/50'}`}
                    title={id === 'produkte' ? (lang === 'de' ? 'Bald verfügbar' : 'Coming soon') : undefined}
                    data-testid={`marke-nav-${id}`}>
                    <Icon className="w-4 h-4 flex-shrink-0" style={{ color: markeSubPage === id ? color : undefined }} />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Right Content */}
            <div className="flex-1 min-w-0">

              {markeSubPage === 'uebersicht' && <BrandDnaView />}


              {markeSubPage === 'kanaele' && <BrandPlattformen getAuthHeader={getAuthHeader} />}

              {markeSubPage === 'strategie' && <StrategyView />}

              {markeSubPage === 'wissen' && <KnowledgeView />}


              {markeSubPage === 'produkte' && (
                <div className="space-y-4">
                  {/* Hauptprodukt */}
                  <Card className="bg-[#1A2B42] border-transparent">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-white text-base flex items-center gap-2"><Crown className="w-4 h-4 text-[#00D9C8]" />{t.settings.products.mainProduct}</CardTitle>
                          <CardDescription className="text-[#A0AEC0] text-sm mt-1">{t.settings.products.mainProductDesc}</CardDescription>
                        </div>
                        {hauptprodukt && (
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-[#5A6C7D] text-xs">{t.settings.products.imageGenReference}</span>
                            <div className="w-10 h-6 rounded-full bg-[#00D9C8] flex items-center px-1">
                              <span className="w-4 h-4 rounded-full bg-white translate-x-4" />
                            </div>
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      {hauptprodukt ? (
                        <div className="bg-[#0A1628] rounded-lg p-4" data-testid="hauptprodukt-display">
                          <div className="flex items-start gap-4">
                            <img src={hauptprodukt.file_url} alt={hauptprodukt.name} className="w-24 h-24 object-cover rounded-lg border border-[#1A2B42]" />
                            <div className="flex-1 space-y-1">
                              <div className="flex items-center gap-2">
                                <p className="text-white font-medium">{hauptprodukt.name}</p>
                                {hauptprodukt.hp_type === 'person' && <span className="text-[10px] bg-purple-500/20 text-[#A0AEC0] px-2 py-0.5 rounded">{t.settings.products.brandFaceBadge}</span>}
                                {hauptprodukt.hp_type === 'product' && <span className="text-[10px] bg-amber-500/20 text-[#00D9C8] px-2 py-0.5 rounded">{t.settings.products.productBadge}</span>}
                              </div>
                              {hauptprodukt.description && <p className="text-[#A0AEC0] text-sm">{hauptprodukt.description}</p>}
                              <p className="text-[10px] text-[#00D9C8]">{t.settings.products.activeAsReference}</p>
                            </div>
                            <Button variant="ghost" size="sm" onClick={handleDeleteHauptprodukt} className="text-red-400 hover:bg-red-500/10 h-8 w-8 p-0" data-testid="delete-hauptprodukt-btn"><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-[#0A1628] rounded-lg p-4 space-y-3 border border-dashed border-[#00D9C8]/20">
                          <div className="flex gap-2">
                            <button onClick={() => setHpType('product')} className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors border ${hpType === 'product' ? 'bg-amber-400/20 text-[#00D9C8] border-amber-400/40' : 'bg-[#1A2B42] text-[#5A6C7D] border-transparent hover:text-[#A0AEC0]'}`} data-testid="hp-type-product">{t.settings.products.product}</button>
                            <button onClick={() => setHpType('person')} className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors border ${hpType === 'person' ? 'bg-[#00D9C8]/20 text-[#00D9C8] border-[#00D9C8]/40' : 'bg-[#1A2B42] text-[#5A6C7D] border-transparent hover:text-[#A0AEC0]'}`} data-testid="hp-type-person">{t.settings.products.person}</button>
                          </div>
                          <Input value={hpName} onChange={(e) => setHpName(e.target.value)} placeholder={hpType === 'person' ? t.settings.products.personNamePh : t.settings.products.productNamePh} className="bg-[#1A2B42] border-transparent text-white text-sm" data-testid="hauptprodukt-name-input" />
                          <Input value={hpDescription} onChange={(e) => setHpDescription(e.target.value)} placeholder={hpType === 'person' ? t.settings.products.rolePositionPh : t.settings.products.shortDescPh} className="bg-[#1A2B42] border-transparent text-white text-sm" data-testid="hauptprodukt-desc-input" />
                          <label className="block">
                            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleUploadHauptprodukt} className="hidden" data-testid="hauptprodukt-file-input" />
                            <div className={`flex items-center justify-center gap-2 px-4 py-3 border border-dashed rounded-lg cursor-pointer transition-colors ${hpType === 'person' ? 'bg-[#1A2B42]/50 border-[#1A2B42] hover:bg-[#1A2B42]' : 'bg-[#00D9C8]/10 border-[#00D9C8]/40 hover:bg-[#00D9C8]/20'}`}>
                              {uploadingHauptprodukt ? <Loader2 className={`w-5 h-5 animate-spin ${hpType === 'person' ? 'text-[#A0AEC0]' : 'text-[#00D9C8]'}`} /> : <Upload className={`w-5 h-5 ${hpType === 'person' ? 'text-[#A0AEC0]' : 'text-[#00D9C8]'}`} />}
                              <span className={`text-sm font-medium ${hpType === 'person' ? 'text-[#A0AEC0]' : 'text-[#00D9C8]'}`}>{uploadingHauptprodukt ? t.settings.products.uploadingText : hpType === 'person' ? t.settings.products.uploadPhoto : t.settings.products.uploadProductImage}</span>
                            </div>
                          </label>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                </div>
              )}


            </div>
          </div>
        </TabsContent>

        {/* ── INTEGRATIONEN TAB ── */}
        <TabsContent value="integrations">
          <Card className="bg-[#0F1D32] border-[#1A2B42]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2"><Link2 className="w-5 h-5 text-[#00D9C8]" />{t.settings.integrations.title}</CardTitle>
                <CardDescription className="text-[#5A6C7D]">{t.settings.integrations.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Social Accounts via Outstand */}
                {[
                  { key: 'linkedin', label: 'LinkedIn', Icon: FaLinkedin, color: '#0A66C2' },
                  { key: 'instagram', label: 'Instagram', Icon: FaInstagram, color: '#E1306C' },
                  { key: 'facebook', label: 'Facebook', Icon: FaFacebook, color: '#1877F2' },
                  { key: 'twitter', label: 'X / Twitter', Icon: FaTwitter, color: '#000000' },
                  { key: 'pinterest', label: 'Pinterest', Icon: FaPinterest, color: '#BD081C' },
                  { key: 'youtube', label: 'YouTube', Icon: FaYoutube, color: '#FF0000' },
                  { key: 'tiktok', label: 'TikTok', Icon: FaTiktok, color: '#FF0050' },
                ].map(({ key, label, Icon, color }) => {
                  const isConnected = connectedPlatforms.includes(key);
                  const isConnecting = connectingPlatform === key;
                  const isDisconnecting = disconnectingPlatform === key;
                  const prereq = PLATFORM_PREREQS[key];
                  return (
                    <div key={key} className="bg-[#1A2B42] rounded-lg p-4 md:p-5" data-testid={`integration-card-${key}`}>
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: color }}>
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="text-white font-medium break-words">{label}</h3>
                            {isConnected ? (
                              <p className="text-xs flex items-center gap-1 text-emerald-400" data-testid={`integration-${key}-status`}>
                                <Check className="w-3 h-3" />
                                <span className="font-medium">{t.settings.integrations.connected}</span>
                              </p>
                            ) : (
                              <p className="text-xs text-[#5A6C7D]" data-testid={`integration-${key}-status`}>{t.settings.integrations.notConnected}</p>
                            )}
                          </div>
                        </div>
                        {isConnected ? (
                          <div className="flex flex-col items-start gap-2 md:flex-row md:items-center">
                            <Badge
                              variant="outline"
                              className="border-emerald-500/40 text-emerald-400 bg-emerald-500/10 w-fit"
                              data-testid={`integration-${key}-connected-badge`}
                            >
                              <Check className="w-3 h-3 mr-1" />{t.settings.integrations.connected}
                            </Badge>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDisconnectPlatform(key)}
                              disabled={isDisconnecting}
                              className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 w-fit"
                              data-testid={`integration-${key}-disconnect-btn`}
                            >
                              {isDisconnecting
                                ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                : <Unlink className="w-3 h-3 mr-1" />}
                              {t.settings.integrations.disconnect}
                            </Button>
                          </div>
                        ) : (
                          <Button
                            onClick={() => {
                              const token = localStorage.getItem('token');
                              setConnectingPlatform(key);
                              window.open(`${API}/integrations/connect/${key}?token=${token}`, '_blank');
                            }}
                            disabled={isConnecting}
                            className="text-white font-medium hover:opacity-80 w-fit"
                            style={{ backgroundColor: color }}
                            data-testid={`integration-${key}-connect-btn`}
                          >
                            {isConnecting
                              ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              : <Icon className="w-4 h-4 mr-1" />}
                            {t.settings.integrations.connect}
                          </Button>
                        )}
                      </div>
                      {prereq && !isConnected && (
                        <p
                          className="mt-3 text-xs text-amber-300/80 bg-amber-500/5 border border-amber-500/20 rounded px-3 py-2"
                          data-testid={`integration-${key}-prereq`}
                        >
                          <strong>{t.settings.integrations.hint}</strong> {prereq}
                        </p>
                      )}
                    </div>
                  );
                })}

                {/* Website integrations — umbrella card grouping Lovable-Website
                    (Supabase webhook) + WordPress + Shopify as an accordion (per-project).
                    The individual cards' logic is unchanged; they render embedded. */}
                <WebsiteIntegrationCard getAuthHeader={getAuthHeader} />

                {/* Disconnect confirm dialog when pending posts exist */}
                {disconnectConfirm && (
                  <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
                    onClick={() => setDisconnectConfirm(null)}
                    data-testid="disconnect-confirm-overlay"
                  >
                    <div
                      className="max-w-md w-full bg-[#0F1E33] border border-amber-500/40 rounded-lg p-6"
                      onClick={(e) => e.stopPropagation()}
                      data-testid="disconnect-confirm-modal"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
                          <AlertCircle className="w-5 h-5 text-amber-400" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-white font-semibold text-lg mb-1">{t.settings.integrations.disconnectWarning.replace('{platform}', disconnectConfirm.platform)}</h3>
                          <p className="text-sm text-slate-300">
                            {disconnectConfirm.pendingCount != null
                              ? t.settings.integrations.pendingPostsWarning.replace('{count}', disconnectConfirm.pendingCount).replace('{s}', disconnectConfirm.pendingCount === 1 ? '' : (lang === 'de' ? 's' : 's'))
                              : disconnectConfirm.message}
                          </p>
                          <p className="text-xs text-slate-500 mt-2">
                            {t.settings.integrations.afterDisconnect}
                          </p>
                          <div className="mt-5 flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-slate-300 hover:bg-slate-800"
                              onClick={() => setDisconnectConfirm(null)}
                              data-testid="disconnect-cancel-btn"
                            >
                              {t.settings.integrations.cancel}
                            </Button>
                            <Button
                              size="sm"
                              className="bg-rose-500 hover:bg-rose-400 text-white"
                              onClick={() => handleDisconnectPlatform(disconnectConfirm.platform, { force: true })}
                              disabled={disconnectingPlatform === disconnectConfirm.platform}
                              data-testid="disconnect-confirm-force-btn"
                            >
                              {t.settings.integrations.disconnectAnyway}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Bild-URLs erneuern — Wartung */}
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 md:p-5" data-testid="refresh-urls-card">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0">
                    <div className="flex items-start md:items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0"><RefreshCw className="w-5 h-5 text-amber-400" /></div>
                      <div className="min-w-0">
                        <h3 className="text-white font-medium break-words">{t.settings.integrations.refreshImageUrlsIntTitle}</h3>
                        <p className="text-xs text-[#A0AEC0] break-words">{t.settings.integrations.refreshImageUrlsIntDesc}</p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm"
                      onClick={async () => {
                        try {
                          setSavingProfile(true);
                          await axios.post(`${API}/admin/refresh-image-urls`, {}, { headers: getAuthHeader(), timeout: 60000 });
                          toast.success(t.settings.integrations.refreshUrlsSuccess);
                        } catch { toast.error(t.settings.integrations.refreshUrlsFailed); }
                        finally { setSavingProfile(false); }
                      }}
                      disabled={savingProfile}
                      className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 w-fit"
                      data-testid="refresh-image-urls-btn-integrations">
                      {savingProfile ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                      {t.settings.integrations.refreshUrls}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
        </TabsContent>

        {/* ── ABRECHNUNG TAB ── */}
        <TabsContent value="abrechnung">
          <BillingTab getAuthHeader={getAuthHeader} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;